import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {ResolvedVisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/ResolvedVisualComponent';
import {IPropsValues} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {IViewportInfo} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/Viewport';
import {IRepeatedData, VisualBox} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/VisualBox';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {HostView} from '@mintdata-wsp/pub/view/HostView';
import {ResizeSensor} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/ResizeSensor';
import {GetPropPathFunc, GetPropStylerFunc} from '@mintdata/mint-kit/api/animation/AnimationProps';
import {
    BaseComponentWithStateContext,
    componentStateContextTypes,
    IComponentStateContext
} from '@mintdata/mint-kit/api/components/BaseComponentWithStateContext';
import {BoxHostId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Size} from '@mintdata/mint-kit/api/models/Size';
import {BaseProps} from '@mintdata/mint-kit/api/properties/BaseProps';
import {PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {PropertyUtils} from '@mintdata/mint-kit/api/properties/PropertyUtils';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {ObjectUtils} from '@mintdata/mint-kit/impl/util/ObjectUtils';
import bind from 'bind-decorator';
import {action, computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';

export interface IBoxViewContentProps
{
    visualBox:VisualBox;
    repeater:Repeater;
    viewportInfo:IViewportInfo;
}

@observer
export class BoxViewContent extends BaseComponentWithStateContext<IBoxViewContentProps, any>
{
    public static readonly childContextTypes = componentStateContextTypes;

    @bind
    private _registerPropStylerFunc(getPropStylerFunc:GetPropStylerFunc):void
    {
        const {visualBox, repeater} = this.props;

        commonControllers.animation.registerComponentPropStylerFunc(visualBox, repeater, getPropStylerFunc);
    }

    @bind
    private _registerPropPathFunc(getPropPathFunc:GetPropPathFunc):void
    {
        const {visualBox, repeater} = this.props;

        commonControllers.animation.registerComponentPropPathFunc(visualBox, repeater, getPropPathFunc);
    }

    @action.bound
    public static onLayoutUpdated()
    {
        const {layoutUpdate} = rootState.ui.visualDesigner.surfaceEditor;

        layoutUpdate.counter += 1;
    }

    @bind
    private _onViewportResize(size:Size):void
    {
        const {visualBox} = this.props;
        const {surfaceArea} = commonControllers;
        const surfaceState:SurfaceState = surfaceArea.getState();

        if( surfaceState
            && surfaceState.activePage
            && surfaceState.activePage.artboard
            && surfaceState.activePage.artboard.viewport.id === visualBox.box.id )
        {
            surfaceArea.setViewportSize(size);
        }
    }

    public shouldComponentUpdate(nextProps:IBoxViewContentProps, nextState:any, nextContext:IComponentStateContext)
    {
        return !ObjectUtils.shallowCompareKeys(this.props, nextProps) ||
            !ObjectUtils.shallowCompareKeys(this.context, nextContext);
    }

    private _renderHostViews():Array<JSX.Element>
    {
        const {visualBox, repeater, viewportInfo} = this.props;
        const hostIds:Array<BoxHostId> = visualBox.box.componentDesc.containerHostIds;
        const isViewportInOverlays:boolean = viewportInfo && viewportInfo.isInOverlays;
        const isViewportOnActivePage:boolean = visualBox.box.isViewport && this._isOnActivePage;
        const allowAnimation:boolean = commonControllers.designState.getDesignState().surface.allowPagesAnimation;

        const hostViews:Array<JSX.Element> = !hostIds ? [] : hostIds.map((hostId:BoxHostId) =>
        {
            return (
                <HostView
                    visualBox={visualBox}
                    key={hostId + 'key'}
                    hostId={hostId}
                    repeater={repeater}
                    isViewportInOverlays={isViewportInOverlays}
                    isViewportOnActivePage={isViewportOnActivePage}
                    allowAnimation={allowAnimation}
                />
            );
        });

        return hostViews;
    }

    public render():JSX.Element
    {
        //TODO: consider isSSRCompatible here

        const {visualBox, repeater, viewportInfo} = this.props;
        const ref = visualBox.box.component.ref;
        const resolvedComponent:ResolvedVisualComponent = ref.resolvedComponent;

        const repeatedData:IRepeatedData = visualBox.repeatedData.get(repeater.index);
        const propsValues:IPropsValues = repeatedData.propsValues;
        const baseProps:BaseProps = propsValues as any;

        const Component:React.ComponentClass = resolvedComponent.componentCtor;

        const {requireSize, supportsAutofocus} = PropertyUtils.getComponentConfig(Component);

        baseProps.getPageInfo = commonControllers.surfaceArea.getPageInfo;
        baseProps.onLayoutUpdated = BoxViewContent.onLayoutUpdated;

        if( supportsAutofocus )
            baseProps.autofocus = this._shouldComponentBeAutofocused;

        if( visualBox.box.isViewport )
            propsValues.viewportProps = viewportInfo;

        baseProps.isDraggable = repeatedData.dnd.isEnabled.value && repeatedData.isDraggable;
        baseProps.surfaceAreaRef = rootState.ui.visualDesigner.surfaceEditor.area.surfaceAreaDomElement;
        baseProps.isPropBound = this._isPropBound;

        baseProps.assets = {
            graphics: commonControllers.assets.graphics.getProps(),
            fonts: commonControllers.assets.fonts.getProps(),
            loadFileIntoComponent: null // TODO: Check this
        };

        baseProps.files = commonControllers.files.getProps();
        baseProps.animation = {
            registerPropStylerFunc: this._registerPropStylerFunc,
            registerPropPathFunc: this._registerPropPathFunc
        };

        if( requireSize )
            return (
                <ResizeSensor {...propsValues} onResize={visualBox.box.isViewport ? this._onViewportResize : null}>
                    <Component>
                        {this._renderHostViews()}
                    </Component>
                </ResizeSensor>
            );
        else
            return (
                <Component {...propsValues}>
                    {this._renderHostViews()}
                </Component>
            );
    }

    @bind
    private _isPropBound(propName:PropName, propPath:PropPath):boolean
    {
        const {visualBox} = this.props;

        const prop:Prop<any> = visualBox.box.componentDesc.properties.get(propName);

        if( !prop )
            return false;

        return prop.isBoundPath(propPath);
    }

    @computed
    private get _isOnActivePage():boolean
    {
        const activePage:Page = commonControllers.designState.getActivePage();

        if( !activePage )
            return false;

        return activePage.id === this.props.visualBox.box.page.id;
    }

    @computed
    private get _shouldComponentBeAutofocused():boolean
    {
        const autofocusPropValue:boolean = this.props.visualBox.box.componentDesc.autofocusProp.value.value;
        if( !autofocusPropValue )
            return false;

        if( this._isOnActivePage )
        {
            const activePage:Page = commonControllers.designState.getActivePage();
            return activePage.overlays.length === 0;
        }

        return commonControllers.designState.isTheTopmostOverlay(this.props.visualBox.box.page.id);
    }
}
